import type { AnyLocaleType } from './locale-type.js';
import type { MessageArgs } from './types.js';

/**
 * The translator's logic, apart from the ICU formatter (ADR-148, concept §5.5). It looks a key up, falls back to
 * English, returns a message with no ICU syntax as it is, runs a **simple** precompiled message itself (a plural
 * included, T-0805), and
 * hands any other message to a formatter it is given. Two translators are made from it:
 * `format.ts` (every message through `intl-messageformat`: the server, the website, the tests) and `lite.ts`
 * (the web entry: simple messages here, the rest through a formatter that a chunk registers, so the
 * formatter's 7 KB is not in the entry).
 */

/** The shape of a catalogue leaf at run time: the ICU source string, or its precompiled message. */
export type CatalogueNode =
  | string
  | readonly ICompiledElement[]
  | { readonly [key: string]: CatalogueNode };

/**
 * A precompiled message is the ICU AST in a compact form, because every entry area's bytes count against the web
 * entry's budget: a literal is its string, an argument `[1, name]`, a tag `[8, name, children]`. A plural stays the
 * parser's object with each option's message compact, and runs here (T-0805); any other element (select, number,
 * date, time) stays the parser's own object, which only the formatter reads.
 * `web/i18nCataloguePlugin.ts` writes it; {@link expandMessage} gives the formatter the AST it expects.
 */
export type ICompiledElement =
  | string
  | readonly [typeof ARGUMENT, string]
  | readonly [typeof TAG, string, readonly ICompiledElement[]]
  | IElement;

/** One element of a precompiled message (`@formatjs/icu-messageformat-parser`'s numbering). */
export interface IElement {
  type: number;
  value?: string;
  children?: readonly IElement[];
  [key: string]: unknown;
}

export interface IFormatter {
  format(args?: Record<string, unknown>): unknown;
}
export type FormatterCtor = new (message: never, locale: string) => IFormatter;

export type TranslateFn = (key: string, args?: MessageArgs) => string;
/**
 * Rich text: the values a message's tags are given (`<address>{email}</address>` takes `address: (chunks) =>
 * node`), and the parts it comes out as. A screen wraps the parts in its own elements; shared never imports
 * React.
 */
export type RichArgs = Record<string, unknown>;
export interface ITranslator extends TranslateFn {
  rich: (key: string, args?: RichArgs) => unknown[];
}

export interface ITranslatorOptions {
  /** Called when `locale` lacks a key `en` has, before the English is used instead. */
  onMissing?: (key: string, locale: string) => void;
}

/** A message with no ICU syntax is returned as it is: no formatter, no parser, no allocation. */
const ICU_SYNTAX = /[{}<]|'[{}<>#]/;

// ICU element types: 0 literal, 1 argument, 6 plural, 7 pound (`#`), 8 tag. Anything else (number, date, time,
// select) needs the formatter.
const LITERAL = 0;
export const ARGUMENT = 1;
export const PLURAL = 6;
const POUND = 7;
export const TAG = 8;

/** One option of a precompiled plural: `one {…}`, `=0 {…}`. Its message is compact too. */
interface IPluralOption {
  value: readonly ICompiledElement[];
}

/** An element's parser type and its fields, whichever form it is in. */
function partsOf(element: ICompiledElement): {
  type: number;
  value?: string;
  children?: readonly ICompiledElement[];
  element?: IElement;
} {
  if (typeof element === 'string') return { type: LITERAL, value: element };
  if (Array.isArray(element)) {
    const compiled = element as unknown as [
      number,
      string,
      readonly ICompiledElement[] | undefined,
    ];
    return { type: compiled[0], value: compiled[1], children: compiled[2] };
  }
  const object = element as IElement;
  return {
    type: object.type,
    value: object.value,
    children: object.children as readonly ICompiledElement[] | undefined,
    element: object,
  };
}

/**
 * Whether a precompiled message is run here: literals, arguments, tags and plurals (T-0805, IN-14), in the
 * compact form or the parser's own. A plural needs no formatter: `Intl.PluralRules` is the runtime's own, and
 * choosing a form is all the formatter does.
 */
export function isSimpleMessage(elements: readonly ICompiledElement[]): boolean {
  return elements.every((item) => {
    const element = partsOf(item);
    return (
      element.type === LITERAL ||
      element.type === ARGUMENT ||
      element.type === POUND ||
      (element.type === TAG && isSimpleMessage(element.children ?? [])) ||
      (element.type === PLURAL &&
        Object.values(element.element?.options as Record<string, IPluralOption>).every((option) =>
          isSimpleMessage(option.value),
        ))
    );
  });
}

/**
 * The compact form of the parser's AST (build time: `web/i18nCataloguePlugin.ts`; the tests use it too). A
 * plural keeps its object, with each option's message compact.
 */
export function compactMessage(
  elements: readonly { type: number; value?: string; children?: unknown }[],
): ICompiledElement[] {
  return elements.map((element): ICompiledElement => {
    if (element.type === LITERAL) return element.value as string;
    if (element.type === ARGUMENT) return [ARGUMENT, element.value as string];
    if (element.type === TAG) {
      return [TAG, element.value as string, compactMessage(element.children as typeof elements)];
    }
    if (element.type === PLURAL) {
      const options = (element as IElement).options as Record<string, { value: typeof elements }>;
      return {
        ...(element as IElement),
        options: Object.fromEntries(
          Object.entries(options).map(([key, option]) => [
            key,
            { ...option, value: compactMessage(option.value) },
          ]),
        ),
      };
    }
    return element as IElement;
  });
}

/** One `Intl.PluralRules` and one `Intl.NumberFormat` per language and kind, made once. */
const pluralRules = new Map<string, Intl.PluralRules>();
const numberFormats = new Map<string, Intl.NumberFormat>();

function pluralRulesFor(locale: string, type: Intl.PluralRuleType): Intl.PluralRules {
  const id = `${locale}\u0000${type}`;
  let rules = pluralRules.get(id);
  if (!rules) {
    rules = new Intl.PluralRules(locale, { type });
    pluralRules.set(id, rules);
  }
  return rules;
}

function numberFormatFor(locale: string): Intl.NumberFormat {
  let format = numberFormats.get(locale);
  if (!format) {
    format = new Intl.NumberFormat(locale);
    numberFormats.set(locale, format);
  }
  return format;
}

/**
 * The option a plural takes for `value`, as `intl-messageformat` chooses it: an exact match (`=0`) first, then
 * the category of the value less the offset in the message's language, then `other`.
 */
function pluralOption(element: IElement, value: number, locale: string): IPluralOption {
  const options = element.options as Record<string, IPluralOption>;
  const exact = options[`=${value}`];
  if (exact) return exact;
  const type = element.pluralType === 'ordinal' ? 'ordinal' : 'cardinal';
  const category = pluralRulesFor(locale, type).select(value - ((element.offset as number) ?? 0));
  return options[category] ?? options.other;
}

function runSimple(
  elements: readonly ICompiledElement[],
  args: RichArgs | undefined,
  locale: string,
  counted?: number,
): unknown[] {
  const parts: unknown[] = [];
  for (const item of elements) {
    const element = partsOf(item);
    if (element.type === LITERAL) parts.push(element.value);
    else if (element.type === ARGUMENT) parts.push(String(args?.[element.value as string] ?? ''));
    else if (element.type === POUND) {
      // `#` is the innermost plural's number, less its offset, in the message's language.
      if (counted !== undefined) parts.push(numberFormatFor(locale).format(counted));
    } else if (element.type === PLURAL) {
      const plural = element.element as IElement;
      const value = Number(args?.[element.value as string]);
      const option = pluralOption(plural, value, locale);
      parts.push(
        ...runSimple(option.value, args, locale, value - ((plural.offset as number) ?? 0)),
      );
    } else {
      const content = runSimple(element.children ?? [], args, locale, counted);
      const handler = args?.[element.value as string];
      if (typeof handler === 'function')
        parts.push((handler as (chunks: unknown[]) => unknown)(content));
      else parts.push(...content);
    }
  }
  return parts;
}

function lookup(messages: unknown, key: string): CatalogueNode | undefined {
  let node: unknown = messages;
  for (const part of key.split('.')) {
    if (node === null || typeof node !== 'object' || Array.isArray(node)) return undefined;
    node = (node as Record<string, unknown>)[part];
  }
  return node as CatalogueNode | undefined;
}

export function buildTranslator(
  locale: AnyLocaleType,
  messages: unknown,
  fallback: { locale: AnyLocaleType; messages: unknown } | undefined,
  options: ITranslatorOptions,
  formatterCtor: () => FormatterCtor | undefined,
  runSimpleHere: boolean,
): ITranslator {
  const formatters = new Map<string, IFormatter>();
  const resolve = (key: string) => {
    let node = lookup(messages, key);
    let from: AnyLocaleType = locale;
    if (node === undefined && fallback) {
      options.onMissing?.(key, locale);
      node = lookup(fallback.messages, key);
      from = fallback.locale;
    }
    return { node, from };
  };
  const format = (
    key: string,
    node: CatalogueNode,
    from: AnyLocaleType,
    args?: RichArgs,
  ): unknown[] => {
    if (
      runSimpleHere &&
      Array.isArray(node) &&
      isSimpleMessage(node as readonly ICompiledElement[])
    ) {
      return runSimple(node as readonly ICompiledElement[], args, from);
    }
    const id = `${from}\u0000${key}`;
    let formatter = formatters.get(id);
    if (!formatter) {
      const Ctor = formatterCtor();
      if (!Ctor) {
        throw new Error(
          `The message "${key}" needs the ICU formatter, which no chunk has registered (select, number, date and time are not for the entry's areas).`,
        );
      }
      formatter = new Ctor(node as never, from);
      formatters.set(id, formatter);
    }
    const out = formatter.format(args);
    return Array.isArray(out) ? out : [out];
  };
  const translate: TranslateFn = (key, args) => {
    const { node, from } = resolve(key);
    if (node === undefined) return key;
    if (typeof node === 'string' && !ICU_SYNTAX.test(node)) return node;
    return format(key, node, from, args as RichArgs | undefined).join('');
  };
  const rich = (key: string, args?: RichArgs): unknown[] => {
    const { node, from } = resolve(key);
    if (node === undefined) return [key];
    if (typeof node === 'string' && !ICU_SYNTAX.test(node)) return [node];
    return format(key, node, from, args);
  };
  return Object.assign(translate, { rich });
}
